import type { AppDispatch } from '@/redux-store'
import { startLogout, resetAuth } from '@/redux-store/slices/auth'
import { clearFarmState } from '@/redux-store/slices/farm'
import { resetAllConfigs } from '@/redux-store/slices/userConfigSlice'
import { resetAllFilters } from '@/redux-store/slices/filtersSlice'
import { farmDataApi } from '@/redux-store/api/farmDataApi'
import { financialDataApi } from '@/redux-store/api/financialDataApi'
import { userConfigApi } from '@/redux-store/api/userConfigApi'
import { openFinanceApi } from '@/redux-store/api/openFinanceApi'
import { categoriesApi } from '@/redux-store/api/categoriesApi'
import { unitsApi } from '@/redux-store/api/unitsApi'

const PERSISTED_KEYS = [
  'persist:app',
  'persist:farmDataApi',
  'persist:userConfigApi',
  'persist:financialDataApi',
  'persist:openFinanceApi',
  'persist:categoriesApi',
  'persist:unitsApi'
] as const

// Flag global consultada por slices que mantêm persistência customizada em localStorage
// para evitar gravações durante o logout (defense-in-depth).
const LOGOUT_FLAG = '__vx_logging_out__'

// Chave usada pelo LoginForm para lembrar o e-mail entre sessões.
// Exportada para que o produtor (LoginForm) e o consumidor (allowlist abaixo)
// compartilhem a MESMA string — evita que um rename silencioso quebre o pareamento.
export const REMEMBERED_EMAIL_KEY = 'vx_remembered_email'

// Chaves do localStorage que devem SOBREVIVER a um logout/reset defensivo do LoginForm.
// `REMEMBERED_EMAIL_KEY` é a única hoje — guarda o e-mail marcado em "Lembrar de mim",
// que precisa cruzar a fronteira logout → próxima visita à tela de login.
// Mantenha este array como a fonte única da verdade — se outras prefs surgirem
// (ex.: idioma, tema), adicione aqui em vez de patchar `clearPersistedStorageKeys`.
const PRESERVED_LOCAL_STORAGE_KEYS = [REMEMBERED_EMAIL_KEY] as const

export const markLoggingOutGlobal = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(LOGOUT_FLAG, '1')
  } catch {
    // ignore
  }
}

export const isLoggingOutGlobal = (): boolean => {
  if (typeof window === 'undefined') return false
  try {
    return window.sessionStorage.getItem(LOGOUT_FLAG) === '1'
  } catch {
    return false
  }
}

export const clearLoggingOutGlobal = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(LOGOUT_FLAG)
  } catch {
    // ignore
  }
}

// Broadcast logout para outras abas. Se BroadcastChannel não existir,
// cai no mecanismo de storage events (localStorage.setItem dispara storage event).
export const broadcastLogout = (): void => {
  if (typeof window === 'undefined') return

  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel('vx_auth')
      channel.postMessage({ type: 'logout', ts: Date.now() })
      channel.close()
    }
  } catch {
    // ignore
  }

  try {
    // Fallback: storage event dispara em outras abas
    window.localStorage.setItem('__vx_logout_signal__', String(Date.now()))
    window.localStorage.removeItem('__vx_logout_signal__')
  } catch {
    // ignore
  }
}

export const dispatchLogoutActions = (dispatch: AppDispatch): void => {
  // PASSO 1: Definir flag de logout PRIMEIRO para impedir qualquer persistência
  dispatch(startLogout())

  // PASSO 2: Reset do auth state
  dispatch(resetAuth())

  // PASSO 3: Reset explícito de cada slice (para garantir que localStorage é limpo)
  dispatch(clearFarmState())
  dispatch(resetAllConfigs())
  dispatch(resetAllFilters())

  // PASSO 4: Reset dos caches RTK Query — TODOS os 5 APIs (was 4, +categoriesApi Phase 4)
  dispatch(farmDataApi.util.resetApiState())
  dispatch(financialDataApi.util.resetApiState())
  dispatch(userConfigApi.util.resetApiState())
  dispatch(openFinanceApi.util.resetApiState())
  dispatch(categoriesApi.util.resetApiState())
  dispatch(unitsApi.util.resetApiState())

  // PASSO 5: Root reducer reset — limpa state + localStorage keys conhecidas
  // @ts-ignore
  dispatch({ type: 'RESET_STORE' })
}

export const clearPersistedStorageKeys = (): void => {
  if (typeof window === 'undefined') return

  try {
    // Snapshot das chaves preservadas ANTES do clear() — `localStorage.clear()` é
    // nuclear e não tem allowlist nativa. Restauramos abaixo para que prefs
    // intencionalmente persistentes (ex.: e-mail do "Lembrar de mim") sobrevivam.
    const preserved: Array<[string, string]> = []
    for (const key of PRESERVED_LOCAL_STORAGE_KEYS) {
      const value = window.localStorage.getItem(key)
      if (value !== null) preserved.push([key, value])
    }

    // Limpar TUDO do localStorage — nuclear option
    window.localStorage.clear()

    // Garantir chaves críticas (caso algo re-popule entre clear e agora)
    const criticalKeys = [
      ...PERSISTED_KEYS,
      'vx_auth_state',
      'farmStateData',
      'selectedFarmID',
      'agri_documents_cache'
    ]

    for (const key of criticalKeys) {
      window.localStorage.removeItem(key)
    }

    // Restaura as chaves preservadas. Feito por último para que nem o clear() nem
    // o loop de removeItem acima as toquem.
    for (const [key, value] of preserved) {
      window.localStorage.setItem(key, value)
    }
  } catch (error) {
    console.error('Erro ao limpar localStorage durante o logout:', error)
  }

  try {
    window.sessionStorage.clear()
  } catch (error) {
    console.error('Erro ao limpar sessionStorage:', error)
  }
}

export const purgeReduxPersistor = async (): Promise<void> => {
  if (typeof window === 'undefined') return

  try {
    const { persistor } = await import('@/redux-store')

    // pause() impede qualquer write após este ponto; depois purge() remove a chave do storage
    persistor.pause()
    await persistor.purge()
  } catch (error) {
    console.error('Erro ao purgar o estado persistido do Redux durante o logout:', error)
  }
}

export const clearNextAuthBrowserCookies = (): void => {
  if (typeof document === 'undefined') return

  // IMPORTANTE: NextAuth v5 (Auth.js) usa prefixo `authjs.*` — NÃO `next-auth.*`.
  // Os cookies reais são: authjs.session-token, authjs.csrf-token, authjs.callback-url.
  // Também são HttpOnly, então JS NÃO CONSEGUE removê-los — só o endpoint
  // /api/auth/signout limpa via Set-Cookie Max-Age=0. Esta função serve como
  // defense-in-depth para qualquer cookie não-HttpOnly remanescente.
  const cookieNames = [
    // Auth.js / NextAuth v5
    'authjs.session-token',
    'authjs.csrf-token',
    'authjs.callback-url',
    'authjs.pkce.code_verifier',
    'authjs.state',
    '__Secure-authjs.session-token',
    '__Secure-authjs.callback-url',
    '__Secure-authjs.csrf-token',
    '__Host-authjs.csrf-token',
    // Legacy NextAuth v4 (compat)
    'next-auth.session-token',
    'next-auth.csrf-token',
    'next-auth.callback-url',
    'next-auth.pkce.code_verifier',
    'next-auth.state',
    '__Secure-next-auth.session-token',
    '__Secure-next-auth.callback-url',
    '__Secure-next-auth.csrf-token',
    '__Host-next-auth.csrf-token'
  ] as const

  const hostname = window.location.hostname
  const parent = hostname.split('.').slice(1).join('.')

  const domains = [hostname, `.${hostname}`, parent, `.${parent}`, ''].filter(d => d !== undefined)

  const paths = ['/', '/pt', '/en', '/es', window.location.pathname]

  cookieNames.forEach(cookieName => {
    domains.forEach(domain => {
      paths.forEach(path => {
        const domainPart = domain ? `; Domain=${domain}` : ''

        document.cookie = `${cookieName}=; Path=${path}${domainPart}; Max-Age=0; SameSite=Lax`
        document.cookie = `${cookieName}=; Path=${path}${domainPart}; Max-Age=0; SameSite=None; Secure`
        document.cookie = `${cookieName}=; Path=${path}${domainPart}; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Lax`
      })
    })
  })
}

export const performReduxAndPersistCleanup = async (dispatch: AppDispatch): Promise<void> => {
  // 1. Marcar flag GLOBAL de logout antes de qualquer coisa para bloquear persistência de slices
  markLoggingOutGlobal()

  // 2. Pausar persistor ANTES de dispatchar resets — impede redux-persist de reescrever
  try {
    const { persistor } = await import('@/redux-store')
    persistor.pause()
  } catch (error) {
    console.error('Erro ao pausar persistor:', error)
  }

  // 3. Dispatcha resets (com persistor pausado, nenhum write vai pro localStorage)
  dispatchLogoutActions(dispatch)

  // 4. Purgar estado persistido (remove a chave 'persist:app' do storage)
  await purgeReduxPersistor()

  // 5. Nuclear clear de localStorage/sessionStorage (remove o LOGOUT_FLAG também)
  clearPersistedStorageKeys()

  // 6. Limpar cookies do NextAuth como defesa em profundidade
  clearNextAuthBrowserCookies()

  // 7. Notificar outras abas
  broadcastLogout()
}
